import React, { useState } from 'react';
import { DownloadAppModal, SharedHero } from '@hover/shared-components';
import { useMediaQuery } from '@chakra-ui/react';
import { navigate } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import { Body, Box } from '@hover/blueprint';
import { Layout, UtilityMenuBanner } from '../../v2/components/common';
import {
  COMPLETE_PROPERTY_DATA_POINTS,
  FITS_INTO_YOUR_PROCESS_POINTS,
  MEASURE_PROFESSIONALS_PARTNERS,
  MEASURE_PROFESSIONALS_PARTNERS_MOBILE,
  MEASURE_PROS,
  MEASURE_RESOURCES,
  MEASURE_TESTIMONIES,
  POWER_TAKEOFFS_POINTS,
  WITHOUT_BREAKING_SWEAT_POINTS,
} from '../../v2/constants/measure';
import SolutionsTestimony from '../../v2/components/common/solutions/testimony/Testimony';
import Points from '../../v2/components/common/solutions/pointsList';
import StartToday from '../../v2/components/common/start-today';
import HoverPros from '../../v2/components/measure/hoverPros';
import MeasureStats from '../../v2/components/measure/stats';
import PartnerList from '../../v2/components/measure/partnerList';
import { URLS } from '../../v2/constants';
import Resources from '../../v2/components/common/solutions/resources';
import { useABTesting } from '../../v2/hooks';
import { ctaABTesting } from '../../v2/utils/ctaABTesting';

const MeasurePage = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [isDesktop] = useMediaQuery('(min-width: 1280px)');
  const [isMobile] = useMediaQuery('(max-width: 480px)');
  const abTestValue = useABTesting({ testName: 'measure-page-cta-pressed' });

  if (!abTestValue) return <>Loading...</>;

  const toggleModal = () => {
    if (!isDesktop) setIsOpen(!isOpen);
    else if (abTestValue === 'A') {
      ctaABTesting('A');
      setIsOpen(!isOpen);
    } else {
      ctaABTesting('B');
      void navigate('/signup');
    }
  };

  return (
    <Layout
      title="3D Interior & Exterior Home Measurements"
      description="More time with customers. Less time measuring. HOVER enables anyone to quickly generate precise interior and exterior property measurements, all from a single app on your phone."
      buttonType="SCHEDULE_A_DEMO"
      navbarDownloadAppURL={URLS.measure.chatWithARep}
      bannerElement={<UtilityMenuBanner />}
    >
      <DownloadAppModal isOpen={isOpen} onClose={toggleModal} />
      <SharedHero
        page="measure"
        bgColor="primary.100"
        eyebrow="MEASUREMENTS"
        heading="More time with customers. Less time measuring."
        body="Exterior and interior property measurements anyone can get, fast."
        openModalButtonTitle="START FREE TRIAL"
        chatWithUsLinkTitle="GET A DEMO"
        py={8}
        chatWithUsLink={URLS.measure.getADemo}
        desktopImage={
          <StaticImage
            loading="eager"
            placeholder="blurred"
            src="../../v2/assets/images/measure_hero.png"
            alt=""
            style={{ maxWidth: '536px' }}
          />
        }
        mobileAndTabletImage={
          <StaticImage
            loading="eager"
            placeholder="blurred"
            src="../../v2/assets/images/measure_hero.png"
            alt=""
          />
        }
        justifyItems="center"
        mb={{ base: '36px', tablet: 1000 }}
      />
      <HoverPros title="Never hand measure again " items={MEASURE_PROS} />
      <Points
        items={COMPLETE_PROPERTY_DATA_POINTS}
        heading="Complete property data"
        url={() => toggleModal()}
        urlTitle="START FREE TRIAL"
        image={
          isMobile ? (
            <StaticImage
              src="../../v2/assets/images/measure_points_1_mobile.png"
              alt=""
            />
          ) : (
            <Box maxW="535px">
              <StaticImage
                src="../../v2/assets/images/measure_points_1_desktop.png"
                alt=""
              />
            </Box>
          )
        }
        isReversed
        pt={{ base: 1000, desktop: 900 }}
        pb={{ base: 800, desktop: 700 }}
        pointsGap="24px"
        pointsGapMobile="20px"
        direction={{ base: 'column-reverse', desktop: 'row' }}
      />
      <Points
        items={WITHOUT_BREAKING_SWEAT_POINTS}
        heading="Without breaking a sweat"
        url={() => toggleModal()}
        urlTitle="START FREE TRIAL"
        image={
          <Box maxW="550px">
            <StaticImage
              src="../../v2/assets/images/measure_points_2.png"
              alt=""
            />
          </Box>
        }
        pt={{ base: 800, desktop: 700 }}
        pb={{ base: 700, desktop: 700 }}
        pointsGap="24px"
        pointsGapMobile="20px"
        gap="3rem"
        direction={{ base: 'column-reverse', desktop: 'row' }}
      />
      <Box
        bg="#E6F1FE"
        w="100%"
        justifyContent="center"
        d={{ base: 'none', tablet: 'flex' }}
        mt={700}
        mb={200}
      >
        <Points
          items={POWER_TAKEOFFS_POINTS}
          heading="Powers takeoffs and more"
          url={() => toggleModal()}
          urlTitle="START FREE TRIAL"
          image={
            <StaticImage
              src="../../v2/assets/images/measure_points_3.png"
              alt=""
            />
          }
          isReversed
          py="92px"
          pointsGap="24px"
          pointsGapMobile="20px"
        />
      </Box>
      <PartnerList
        partners={
          isMobile
            ? MEASURE_PROFESSIONALS_PARTNERS_MOBILE
            : MEASURE_PROFESSIONALS_PARTNERS
        }
        heading="The most trusted names rely on HOVER"
        desktopRows={5}
      />
      <SolutionsTestimony items={MEASURE_TESTIMONIES} />
      <Box
        bg="#E6F1FE"
        w="100%"
        justifyContent="center"
        d={{ base: 'flex', tablet: 'none' }}
        mb={900}
      >
        <Points
          items={POWER_TAKEOFFS_POINTS}
          heading="Powers the tools you need"
          url={() => toggleModal()}
          urlTitle="START FREE TRIAL"
          image={
            <StaticImage
              src="../../v2/assets/images/measure_points_5.png"
              alt=""
            />
          }
          isReversed
          py={900}
          pointsGap="24px"
          pointsGapMobile="20px"
          direction={{ base: 'column-reverse', desktop: 'row' }}
        />
      </Box>
      <Points
        items={FITS_INTO_YOUR_PROCESS_POINTS}
        heading="Fits into your process"
        url={() => toggleModal()}
        urlTitle="START FREE TRIAL"
        image={
          <Box
            w={{ base: '100%', tablet: '534px' }}
            overflow="hidden"
            borderRadius="8px"
          >
            <StaticImage
              src="../../v2/assets/images/measure_points_4.png"
              alt=""
            />
          </Box>
        }
        pt={{ base: 700, desktop: 0 }}
        pb={{ base: 800, desktop: 900 }}
        pointsGap="24px"
        pointsGapMobile="20px"
      />
      <MeasureStats />
      <StartToday
        heading="Start Your Trial Today"
        body="Sign up to improve efficiency and increase close rates for you and your team."
        cta={{
          label: 'START FREE TRIAL',
          href: () => toggleModal(),
        }}
      />
      <Resources
        items={MEASURE_RESOURCES}
        bg="none"
        mb={{ base: 0, tablet: 1000, desktop: '76px' }}
      />
      <Body
        mb={800}
        maxW={{ base: 'calc(100% - 32px)', tablet: '100%' }}
        display="flex"
        justifyContent="center"
      >
        <Box
          as="span"
          fontSize={{ base: '12px' }}
          mr={{ base: '2px' }}
          mt={{ base: '-6px' }}
          color="#767676"
        >
          ‡
        </Box>{' '}
        <Body
          size={{ base: 300, tablet: 400 }}
          color="#767676"
          textAlign="center"
          maxW={{ base: 'calc(100% - 32px)', tablet: '100%' }}
          display="flex"
        >
          HOVER may request a reference measurement to validate specific
          properties.{' '}
        </Body>
      </Body>
    </Layout>
  );
};

export default MeasurePage;
